import React, { ComponentProps, useMemo, useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/pro-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import Dropdown from "ui/Dropdown";
import Button from "ui/Button";
import { ItemRendererProps } from "./types";

import styles from "./style.module.scss";
import { makeFilter } from "utils/filter";
import classNames from "classnames";
import { ExpandToggle } from "ui/Icons";

const FILTER_THRESHOLD = 5;

export type Section<T> = {
  label?: string;
  entries: T[];
  hideOnFilter?: boolean;
  icon?: IconDefinition;
  collapsible?: boolean;
};

export function MenuList<T extends { id: string; parentId?: string | null }>({
  trigger,
  prompt,
  noMatch,
  sections,
  filterKeys,
  renderer: ItemRenderer,
  children: extraItems,
  dropdownWidth,
  collapsedSections: controlledCollapsed,
  onToggleCollapsed,
}: {
  trigger: ComponentProps<typeof Dropdown>["trigger"];
  prompt: string;
  noMatch?: string;
  sections: Section<T>[];
  filterKeys: (keyof T)[];
  renderer: React.ComponentType<ItemRendererProps<T>>;
  children?: React.ReactNode;
  dropdownWidth?: number;
  collapsedSections?: string[];
  onToggleCollapsed?: (label: string) => void;
}) {
  const [filter, setFilter] = useState("");
  const [internalCollapsed, setInternalCollapsed] = useState(new Set<string>());
  const isControlled = controlledCollapsed !== undefined;
  const collapsedSections = useMemo(
    () => (isControlled ? new Set(controlledCollapsed) : internalCollapsed),
    [isControlled, controlledCollapsed, internalCollapsed],
  );
  const toggleCollapsed = (label: string) => {
    if (isControlled) {
      onToggleCollapsed?.(label);
      return;
    }
    setInternalCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };
  const totalEntries = sections.reduce((n, s) => n + s.entries.length, 0);
  const doFilter = totalEntries > FILTER_THRESHOLD;

  const filterActive = filter !== "";
  const filteredSections = useMemo(() => {
    const visible = filterActive
      ? sections.filter((s) => !s.hideOnFilter)
      : sections;
    if (!doFilter) {
      return visible;
    }
    return visible
      .map((section) => ({
        ...section,
        entries: filterEntries(section.entries, filter, filterKeys),
      }))
      .filter((section) => section.entries.length > 0);
  }, [sections, filter, filterActive, doFilter, filterKeys]);

  const totalFilteredEntries = filteredSections.reduce(
    (n, s) => n + s.entries.length,
    0,
  );

  function resetFilter() {
    setFilter("");
  }

  const listClass = classNames(
    "mb-0 relative list-none",
    doFilter && "max-h-[55.25vh] overflow-y-auto",
  );

  return (
    <Dropdown
      trigger={trigger}
      className={styles.dropdown}
      onClose={resetFilter}
    >
      {({ setClosed }) => {
        return (
          <div
            className={styles.selectMenuList}
            style={{
              ...(dropdownWidth !== undefined && { width: dropdownWidth }),
            }}
          >
            {doFilter && (
              <ListFilter
                prompt={prompt}
                filter={filter}
                setFilter={setFilter}
              />
            )}
            {(doFilter || totalFilteredEntries > 0) && (
              <ul className={listClass}>
                {doFilter && totalFilteredEntries === 0 ? (
                  <li>
                    <div
                      className={classNames(
                        styles.selectMenuListItem,
                        styles.noMatches,
                      )}
                    >
                      <span>&mdash; {noMatch ?? "no matches"} &mdash;</span>
                    </div>
                  </li>
                ) : (
                  filteredSections.map((section, sectionIdx) => {
                    const isCollapsed =
                      !filterActive &&
                      !!section.collapsible &&
                      !!section.label &&
                      collapsedSections.has(section.label);
                    return (
                      <React.Fragment key={section.label ?? sectionIdx}>
                        {section.label && (
                          <SectionLabel
                            label={section.label}
                            icon={section.icon}
                            collapsible={section.collapsible}
                            collapsed={isCollapsed}
                            onToggle={() =>
                              section.label && toggleCollapsed(section.label)
                            }
                          />
                        )}
                        {!isCollapsed &&
                          section.entries.map((item) => (
                            <li key={`${sectionIdx}-${item.id}`}>
                              <ItemRenderer
                                {...{ item, onSelect: setClosed }}
                              />
                            </li>
                          ))}
                      </React.Fragment>
                    );
                  })
                )}
              </ul>
            )}
            {extraItems}
          </div>
        );
      }}
    </Dropdown>
  );
}

function filterEntries<T extends { id: string; parentId?: string | null }>(
  entries: T[],
  filter: string,
  filterKeys: (keyof T)[],
): T[] {
  const matchingIds = new Set(
    entries.filter(makeFilter(filter, ...filterKeys)).map((item) => item.id),
  );

  // Include parents of matching children
  for (const item of entries) {
    if (matchingIds.has(item.id) && item.parentId) {
      matchingIds.add(item.parentId);
    }
  }

  // Include children of matching parents
  for (const item of entries) {
    if (item.parentId && matchingIds.has(item.parentId)) {
      matchingIds.add(item.id);
    }
  }

  return entries.filter((item) => matchingIds.has(item.id));
}

const SectionLabel = ({
  label,
  icon,
  collapsible,
  collapsed,
  onToggle,
}: {
  label: string;
  icon?: IconDefinition;
  collapsible?: boolean;
  collapsed?: boolean;
  onToggle?: () => void;
}) => {
  const outerClass =
    "mt-[9px] !border-0 !border-t !border-solid !border-[#e8e8ee] first:mt-0 first:!border-t-0 uppercase text-[11px] font-semibold tracking-wide text-[#999] leading-[11px] !pt-[15px] !pb-[9px]";
  const innerFlex = "flex items-center";
  const children = (
    <>
      {icon && <FontAwesomeIcon icon={icon} className="mr-[6px] text-[12px]" />}
      <span>{label}</span>
      {collapsible && (
        <ExpandToggle expanded={!collapsed} className="ml-auto text-[14px]" />
      )}
    </>
  );
  if (collapsible) {
    return (
      <Button
        bare
        type="button"
        onClick={onToggle}
        className={classNames(outerClass, innerFlex, "!w-full !min-w-0")}
      >
        {children}
      </Button>
    );
  }
  return <div className={classNames(outerClass, innerFlex)}>{children}</div>;
};

const ListFilter = ({
  prompt,
  filter,
  setFilter,
}: {
  prompt: string;
  filter: string;
  setFilter: (v: string) => void;
}) => {
  // We want to focus the input field when the menu first opens; i.e.,
  // (currently) when this component is first rendered. We want to avoid the
  // autofocus behavior on touch devices, since it's probably more convenient to
  // scroll through the recent item list. We don't bother to subscribe to events
  // for the query, because this is not likely to change in the course of a
  // single run of our application on a specific device.
  const focusInput = window.matchMedia("(pointer: fine)");

  const inputRef = (input: HTMLInputElement | null) => {
    // Focus the input after component mounts. preventScroll avoids a
    // synchronous scroll-into-view, which could be expensive, and the
    // nav is always in view anyway.
    if (focusInput.matches && input) {
      input.focus({ preventScroll: true });
    }
  };

  return (
    <div className="flex items-center leading-5 text-base mt-2 mb-1 pb-2 border-b border-[#ccc]">
      <FontAwesomeIcon
        className="flex-none text-sm text-[#ccc] mr-1.5"
        icon={faMagnifyingGlass}
      />
      <input
        ref={inputRef}
        className="flex-grow flex-shrink min-w-0"
        placeholder={prompt}
        type="text"
        value={filter}
        onChange={(e) => setFilter(e.currentTarget.value)}
      />
    </div>
  );
};

export default MenuList;
